import React, { useState, useEffect, useContext } from 'react';
import {
  Box, TextField, InputAdornment, Typography, IconButton, Tooltip, Button, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import Checkbox from '@mui/material/Checkbox';
import ReusableTable from '../components/tables/resuabletable';
import { AuthContext } from '..';
import axios from 'axios';
import { format, parse } from 'date-fns';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Swal from 'sweetalert2';

export default function SalesHistory() {
  const {token} = useContext(AuthContext)
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const { companyid, companydata } = useContext(AuthContext);
  const [pdf] = useState(true);
  const [editicon] = useState(false);
  const [deleteicon] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 3,
  });
  const [totalrows, setTotalrows] = useState(0);
  const [opencolspaper, setOpencolspaper] = useState(false);
  const [columnOrder, setColumnOrder] = useState([]);
  const [openEmailDialog, setOpenEmailDialog] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [selectedSale, setSelectedSale] = useState(null);
  const [invoicedialog, setInvoicedialog] = useState(false);
  const [customers, setCustomers] = useState([]);

  const handlePaginationChange = (paginationData) => {
    setPagination(paginationData);
  };

  useEffect(() => {
    if (companyid) {
      fetchSalesHistory();
      fetchCustomers();
    }
  }, [appliedFilters, pagination, companyid]);

  const fetchSalesHistory = () => {
    const payload = {
      filters: JSON.stringify(appliedFilters),
      currentpage: pagination.page,
      rowsPerPage: pagination.rowsPerPage,
    };
    axios
      .get(`${process.env.REACT_APP_API_LINKS}/sales/saleshistory`, { params: payload, withCredentials: true,headers:{Authorization:`bearer ${token}`} })
      .then((res) => {
        const columns = res.data?.columns || [];
        const data = res.data?.data || [];

        setColumns(
          columns.map((col) => {
            if (col.field === 'invoice_date') return { ...col, field: 'display_invoiceDate' };
            return col;
          })
        );
        setColumnOrder(columns.filter((col) => !col.hide).map((col) => col.field));
        setTotalrows(res.data.totalrows?.[0]?.totalusers || 0);

        const formattedData = data.map((sale) => {
          let invoiceDateFormatted = '';
          if (sale.invoice_date) {
            const parsed = parse(sale.invoice_date, 'dd/MM/yyyy', new Date());
            invoiceDateFormatted = format(parsed, 'dd/MM/yyyy');
          }

          let createdOnFormatted = '';
          if (sale.created_on) {
            const parsedCreated = parse(sale.created_on, 'dd/MM/yyyy HH:mm', new Date());
            createdOnFormatted = format(parsedCreated, 'yyyy-MM-dd HH:mm');
          }

          return {
            ...sale,
            invoiceDate: invoiceDateFormatted,
            createdOn: createdOnFormatted,
            customerName: sale.client_name || '',
            createdByName: sale.created_by_name || '',
            display_invoiceDate: invoiceDateFormatted,
          };
        });
        setRows(formattedData);
      })
      .catch((err) => {
        console.error('Failed to fetch sales history:', err);
        Swal.fire('Error', 'Failed to fetch sales history data.', 'error');
      });
  };

  const fetchCustomers = () => {
    axios
      .get(`${process.env.REACT_APP_API_LINKS}/clients/getcompanyclients/${companyid}`, { withCredentials: true })
      .then((res) => {
        const formatted = (res.data.data || []).map((row) => ({
          id: row.id,
          name: row.client_name,
          contactPerson: row.contact_person,
          email: row.email,
          phone: row.phone,
          services: row.services,
          revisedAmounts: row.revisedAmounts,
        }));
        setCustomers(formatted);
      })
      .catch((err) => {
        console.error('Failed to fetch customers:', err);
        setCustomers([]);
      });
  };

  const filteredColumns = columnOrder
    .map((field) => columns.find((col) => col.field === field))
    .filter((col) => col && !col.hide);

  const exportToExcel = () => {
    const exportData = filteredRows.map((row, index) => {
      const formattedRow = { 'S.No': index + 1 };
      filteredColumns.forEach((col) => {
        if (col.headerName !== 'S.No') {
          formattedRow[col.headerName || col.field] = row[col.field] || '';
        }
      });
      return formattedRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'SalesHistory');
    XLSX.writeFile(workbook, `sales_history_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handlePdf = async (sale, action, dynamicSubject) => {
    if (!action || action === 'cancel') return;

    try {
      const bankRes = await axios.get(
        `${process.env.REACT_APP_API_LINKS}/settings/bank-account`,
        {
          headers:
          {
            Authorization: `bearer ${token}`
          },
          withCredentials: true
        }
      );

      const bankData = bankRes.data?.bankData;

      const settingsRes = await axios.get(
        `${process.env.REACT_APP_API_LINKS}/settings/getsettings`,
        {
          headers:
          {
            Authorization: `bearer ${token}`
          },
          withCredentials: true
        }
      );
      if (settingsRes.status !== 200) throw new Error('Failed to fetch company settings');
      const settings = settingsRes.data;
      const customer = customers.find((c) => c.id === sale.customer_id) || {};
      const services = Array.isArray(customer.services)
        ? customer.services
        : typeof customer.services === 'string'
          ? JSON.parse(customer.services)
          : [];
      let revisedAmounts = [];
      try {
        revisedAmounts = typeof customer.revisedAmounts === 'string'
          ? JSON.parse(customer.revisedAmounts || '[]')
          : Array.isArray(customer.revisedAmounts)
            ? customer.revisedAmounts
            : [];
      } catch (err) {
        console.error('Failed to parse revisedAmounts:', err);
        revisedAmounts = [];
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const marginX = 20;
      const startY = 50;
      const endY = pageHeight - 40;
      let currentY = startY;

      const checkAddPage = (blockHeight = 10) => {
        if (currentY + blockHeight > endY) {
          pdf.addPage();
          currentY = startY;
          if (bgImage.src) pdf.addImage(bgImage, 'PNG', 0, 0, pageWidth, pageHeight);
        }
      };

      // Background Image
      const bgUrl = `${process.env.REACT_APP_API_LINKS}/headers/${settings?.logo || ''}`;
      const bgImage = new Image();
      bgImage.crossOrigin = 'anonymous';
      bgImage.src = bgUrl;
      const bgLoaded = await new Promise((resolve) => {
        bgImage.onload = () => resolve(true);
        bgImage.onerror = () => resolve(false);
      });

      if (bgLoaded) {
        pdf.addImage(bgImage, "PNG", 0, 0, pageWidth, pageHeight);
      }
      // Header
      pdf.setFont('Times', 'bold');
      pdf.setFontSize(18);
      pdf.text('Sales History Invoice', pageWidth / 2, currentY - 10, { align: 'center' });

      // Dynamic Subject
      pdf.setFont('Times', 'bold');
      pdf.setFontSize(14);
      const subject = dynamicSubject || `Sales Invoice ${sale.invoice_number}`;
      const subjectLines = pdf.splitTextToSize(subject, pageWidth - 2 * marginX);
      checkAddPage(subjectLines.length * 6);
      pdf.text(subjectLines, marginX, currentY);
      currentY += subjectLines.length * 6 + 8;

      // Customer Info
      pdf.setFont('Times', 'normal');
      pdf.setFontSize(12);
      checkAddPage(30);
      pdf.text(`To: ${customer.name || ''}`, marginX, currentY);
      pdf.text(`Contact: ${customer.contactPerson || ''}`, marginX, currentY + 6);
      pdf.text(`Email: ${customer.email || ''}`, marginX, currentY + 12);
      pdf.text(`Phone: ${customer.phone || ''}`, marginX, currentY + 18);
      pdf.text(
        `Date: ${sale.invoice_date ? sale.invoice_date : new Date().toLocaleDateString()}`,
        pageWidth - marginX,
        currentY,
        { align: 'right' }
      );
      currentY += 28;

      // Services Table
      const serviceColumns = ['Service Name', 'Start Date', 'End Date', 'Total Amount'];
      const serviceRows = services.map((s) => [
        s.service_name || '-',
        s.start_date ? format(new Date(s.start_date), 'dd/MM/yyyy') : '-',
        s.end_date ? format(new Date(s.end_date), 'dd/MM/yyyy') : '-',
        parseFloat(s.total_amount || 0).toFixed(2),
      ]);

      autoTable(pdf, {
        startY: currentY,
        head: [serviceColumns],
        body: serviceRows,
        margin: { left: marginX, right: marginX },
        styles: {
          font: 'times',
          fontSize: 10,
          textColor: [0, 0, 0],
          cellPadding: 3,
          lineColor: [0, 0, 0],
          lineWidth: 0.3,
        },
        alternateRowStyles: { fillColor: null },
        headStyles: { fontStyle: 'bold', halign: 'center', fillColor: null },
        columnStyles: {
          0: { cellWidth: 70 },
          1: { cellWidth: 35 },
          2: { cellWidth: 35 },
          3: { cellWidth: 35, halign: 'right' },
        },
      });
      currentY = pdf.lastAutoTable.finalY + 12;

      // Financial Summary
      pdf.setFont('Times', 'bold');
      pdf.text('Financial Summary:', marginX, currentY);
      pdf.setFont('Times', 'normal');

      const financialColumns = ['Description', 'Amount'];
      const financialRows = [
        ...revisedAmounts.map((entry) => [
          entry.description,
          entry.description.includes('%') ? entry.amount : parseFloat(entry.amount).toFixed(2),
        ]),
        ['Previously Paid', parseFloat(sale.paid_amount || 0).toFixed(2)],
        ['Current Paying', parseFloat(sale.currently_paying || 0).toFixed(2)],
        ['Remaining Balance', parseFloat(sale.remaining_balance || 0).toFixed(2)],
      ];
      autoTable(pdf, {
        startY: currentY + 6,
        head: [financialColumns],
        body: financialRows,
        margin: { left: marginX, right: marginX },
        styles: {
          font: 'times',
          fontSize: 10,
          textColor: [0, 0, 0],
          cellPadding: 3,
          lineColor: [0, 0, 0],
          lineWidth: 0.3,
          fillColor: null,
        },
        headStyles: { fontStyle: 'bold', halign: 'center', fillColor: null },
        alternateRowStyles: { fillColor: null },
        columnStyles: {
          0: { cellWidth: 100 },
          1: { cellWidth: 75, halign: 'right' },
        },
      });
      currentY = pdf.lastAutoTable.finalY + 12;

      if (bankData) {
        pdf.setFont("Times", "italic");
        pdf.setFontSize(10);
        const paymentNote = "You can make the payment using the following bank details:";
        checkAddPage(10);
        pdf.text(paymentNote, marginX, currentY);
        currentY += 6;

        pdf.setFont("Times", "normal");
        pdf.setFontSize(10);

        const bankDetailsRows = [
          [`Bank Name: ${bankData.bankName || "-"}`, `A/C Holder: ${bankData.accountHolderName || "-"}`],
          [`A/C Number: ${bankData.accountNumber || "-"}`, `Account Type: ${bankData.accountType || "-"}`],
          [`IFSC: ${bankData.ifscCode || "-"}`, `MICR: ${bankData.micrCode || "-"}`],
          [`Branch Name: ${bankData.branchName || "-"}`, `Branch Address: ${bankData.branchAddress || "-"}`],
        ];

        const rowHeight = 6;
        const columnWidth = (pageWidth - 2 * marginX) / 2;

        for (const row of bankDetailsRows) {
          checkAddPage(rowHeight);
          pdf.text(row[0], marginX, currentY);
          pdf.text(row[1], marginX + columnWidth, currentY);
          currentY += rowHeight;
        }

        currentY += 6;
      }

      // Output
      const fileName = `SalesHistory_Invoice_${sale.invoice_number}_${new Date().toISOString().split('T')[0]}.pdf`;
      if (action === 'whatsapp') {
        alert('WhatsApp feature will be added soon');
      } else if (action === 'download') {
        pdf.save(fileName);
        Swal.fire('Downloaded!', '', 'success');
      } else if (action === 'preview') {
        window.open(pdf.output('bloburl'), '_blank');
      } else if (action === 'send') {
        const pdfBlob = pdf.output('blob');
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64PDF = reader.result.split(',')[1];
          const payload = {
            to: customer.email || '',
            subject: dynamicSubject || `Sales History Invoice - ${companydata[0]?.companyname || 'Your Company'}`,
            message: emailContent || `Dear ${customer.contactPerson || customer.name},\n\nPlease find attached the sales history invoice details.\n\nBest regards,\n${companydata[0]?.companyname || 'Your Company'}`,
            pdfBase64: base64PDF,
            fileName,
            client: customer,
            services,
            companydata,
            settings,
          };
          try {
            const emailRes = await axios.post(
              `${process.env.REACT_APP_API_LINKS}/invoice/sendinvoice`,
              payload,
              { withCredentials: true }
            );
            if (emailRes.status === 200) {
              Swal.fire('Sent!', 'Invoice has been emailed.', 'success');
            } else {
              Swal.fire('Failed!', 'Could not send email.', 'error');
            }
          } catch (err) {
            Swal.fire('Error!', 'An error occurred sending the email.', 'error');
          }
        };
        reader.readAsDataURL(pdfBlob);
      }
    } catch (err) {
      console.error('PDF generation failed:', err);
      Swal.fire('Error', 'Something went wrong!', 'error');
    }
  };

  const handleOpenEmailDialog = (sale) => {
    setSelectedSale(sale);
    const customer = customers.find((c) => c.id === sale.customer_id) || {};
    setEmailSubject(`Sales History Invoice - ${new Date().toISOString().split('T')[0]}`);
    setEmailContent(
      `Dear ${customer.contactPerson || customer.name || 'Customer'},\n\n` +
      `Attached is the sales history invoice detailing your services and payment summary.\n\n` +
      `Please review and contact us for any queries.\n\nBest regards,\n${companydata[0]?.companyname || 'Your Company'}`
    );
    setOpenEmailDialog(true);
  };

  const handleCloseEmailDialog = () => {
    setOpenEmailDialog(false);
    setEmailSubject('');
    setEmailContent('');
    setSelectedSale(null);
  };

  const handleSendEmail = () => {
    if (!emailSubject.trim()) {
      Swal.fire('Error', 'Subject is required', 'error');
      return;
    }
    if (!emailContent.trim()) {
      Swal.fire('Error', 'Email content is required', 'error');
      return;
    }
    handlePdf(selectedSale, 'send', emailSubject);
    handleCloseEmailDialog();
  };

  const maildialogbox = (sale) => {
    setSelectedSale(sale);
    setInvoicedialog(true);
  };

  const closemaildialogbox = () => {
    setInvoicedialog(false);
  };

  const filteredRows = rows
    .filter((row) => {
      const invoiceNumber = row.invoice_number?.toLowerCase() || '';
      const customerName = row.customerName?.toLowerCase() || '';
      const invoiceDate = row.invoiceDate?.toLowerCase() || '';

      return (
        invoiceNumber.includes(searchQuery.toLowerCase()) ||
        customerName.includes(searchQuery.toLowerCase()) ||
        invoiceDate.includes(searchQuery.toLowerCase())
      );
    })
    .map((row) => ({
      ...row,
      display_invoiceDate: row.invoiceDate || '',
    }));

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          mb: 2,
        }}
      >
        <TextField
          placeholder="Search by Invoice No or Customer"
          size="small"
          variant="outlined"
          sx={{ minWidth: 200, width: { sm: 400, md: 500 } }}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'row', sm: 'row', md: 'row' },
            justifyContent: { xs: 'flex-start', sm: 'space-between', md: 'flex-end' },
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
          }}
        >
          <Tooltip title="Export to Excel" arrow>
            <IconButton onClick={exportToExcel}>
              <DownloadIcon sx={{ color: '#0b85c8' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Customize Columns" arrow>
            <IconButton
              onClick={() => setOpencolspaper(true)}
              sx={{ color: '#0b85c8', padding: '8px' }}
            >
              <ViewColumnIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {opencolspaper && (
        <Paper
          elevation={3}
          sx={{
            p: 2,
            zIndex: 1300,
            borderRadius: 2,
            position: 'absolute',
            top: { lg: '100px', xs: '200px', sm: '150px', md: '150px' },
            right: '25px',
            width: { xs: '284px', sm: '320px', md: '280px' },
            maxWidth: '320px',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontFamily: 'Open Sans',
                fontWeight: 600,
                color: '#0b85c8',
                fontSize: { xs: '13px', sm: '14px', md: '15px' },
              }}
            >
              Customize Columns
            </Typography>
            <IconButton onClick={() => setOpencolspaper(false)} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ mb: 1 }}>
            <Button
              variant="text"
              onClick={() => {
                const allSelected = columns.every((column) => !column.hide);
                if (allSelected) {
                  const updatedColumns = columns.map((column) => ({ ...column, hide: true }));
                  setColumns(updatedColumns);
                  setColumnOrder([]);
                } else {
                  const updatedColumns = columns.map((column) => ({ ...column, hide: false }));
                  setColumns(updatedColumns);
                  setColumnOrder(columns.map((column) => column.field));
                }
              }}
              sx={{
                fontFamily: 'Open Sans',
                fontSize: { xs: '11px', sm: '12px', md: '13px' },
                textTransform: 'none',
                color: '#0b85c8',
                p: 0,
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                },
              }}
            >
              {columns.every((column) => !column.hide) ? 'Unselect All' : 'Select All'}
            </Button>
          </Box>
          <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
            {columns.map((column) => (
              <Box key={column.field} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Checkbox
                  checked={!column.hide}
                  onChange={(e) => {
                    const updatedColumns = columns.map((col) =>
                      col.field === column.field ? { ...col, hide: !e.target.checked } : col
                    );
                    setColumns(updatedColumns);
                    if (e.target.checked) {
                      setColumnOrder((prev) => {
                        if (!prev.includes(column.field)) {
                          return [...prev, column.field];
                        }
                        return prev;
                      });
                    } else {
                      setColumnOrder((prev) => prev.filter((field) => field !== column.field));
                    }
                  }}
                  sx={{ mr: 1, p: 0.5 }}
                />
                <Typography
                  sx={{
                    fontFamily: 'Open Sans',
                    fontSize: { xs: '12px', sm: '13px', md: '14px' },
                  }}
                >
                  {column.headerName || column.field}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      <Box sx={{ overflowX: 'auto', width: '100%' }}>

        <ReusableTable
          columns={filteredColumns}
          rows={filteredRows}
          editicon={editicon}
          deleteicon={deleteicon}
          pdf={pdf}
          onPdf={maildialogbox}
          onApplyFilters={(activeFilters) => {
            setAppliedFilters(activeFilters);
          }}
          onPaginationChange={handlePaginationChange}
          totalrows={totalrows}
        />
      </Box>

      <Dialog open={invoicedialog} onClose={closemaildialogbox}>
        <DialogTitle sx={{ textAlign: 'center', fontFamily: 'Open Sans', fontSize: '18px' }}>
          What would you like to do?
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 1 }}>
            <Tooltip title="Send Mail">
              <IconButton
                color="primary"
                onClick={() => {
                  closemaildialogbox();
                  handleOpenEmailDialog(selectedSale);
                }}
              >
                <MailOutlineIcon />
              </IconButton>
            </Tooltip>
            {/* <Tooltip title="WhatsApp">
              <IconButton
                color="success"
                onClick={() => {
                  handlePdf(selectedSale, 'whatsapp');
                  closemaildialogbox();
                }}
              >
                <WhatsAppIcon />
              </IconButton>
            </Tooltip> */}
            <Tooltip title="Preview PDF">
              <IconButton
                color="info"
                onClick={() => {
                  handlePdf(selectedSale, 'preview');
                  closemaildialogbox();
                }}
              >
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download PDF">
              <IconButton
                color="info"
                onClick={() => {
                  handlePdf(selectedSale, 'download');
                  closemaildialogbox();
                }}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Cancel">
              <IconButton
                color="error"
                onClick={closemaildialogbox}
              >
                <CancelIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openEmailDialog}
        onClose={handleCloseEmailDialog}
        maxWidth="sm"
        fullWidth
        sx={{ '& .MuiDialog-paper': { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontFamily: 'Open Sans', textAlign: 'center', color: '#0b85c8', backgroundColor: '#f0f9ff' }}>
          Compose Email
        </DialogTitle>
        <DialogContent sx={{ fontFamily: 'Open Sans', backgroundColor: '#f0f9ff', pt: 2 }}>
          <TextField
            label="Subject"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Email Content"
            value={emailContent}
            onChange={(e) => setEmailContent(e.target.value)}
            fullWidth
            multiline
            rows={6}
            size="small"
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', backgroundColor: '#f0f9ff', pb: 2 }}>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSendEmail}
            sx={{ backgroundColor: '#0b85c8', fontFamily: 'Open Sans' }}
          >
            Send Email
          </Button>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={handleCloseEmailDialog}
            sx={{ fontFamily: 'Open Sans', color: '#0b85c8', borderColor: '#0b85c8' }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}